// frontend/src/components/chat/ChatWindow.js
// Ventana principal de chat - Estilo Messenger

import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS, apiRequest } from '../../config/api';
import ConversationList from './ConversationList';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ChatHeader from './ChatHeader';
import NewChatModal from './NewChatModal';
import ChatSecurityModal from './ChatSecurityModal';
import ChatLoadingAnimation from './ChatLoadingAnimation';
import './ChatWindow.css';

const ChatWindow = ({ user, onMinimize, onClose, onUnreadChange }) => {
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [showNewChat, setShowNewChat] = useState(false);
    const [showSidebar, setShowSidebar] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [replyTo, setReplyTo] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isExitingFullscreen, setIsExitingFullscreen] = useState(false);

    // Estados de seguridad (Fase 1)
    const [securityState, setSecurityState] = useState('checking'); // 'checking', 'needsVerification', 'loading', 'ready'
    const [showSecurityModal, setShowSecurityModal] = useState(false);
    const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
    const [isFirstTimeDevice, setIsFirstTimeDevice] = useState(true);

    // Detectar cambio de tamano de pantalla
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ========================================================
    // FASE 1: Verificacion de seguridad del dispositivo
    // ========================================================
    const generateDeviceFingerprint = useCallback(() => {
        const userAgent = navigator.userAgent;
        const data = `${userAgent}-${navigator.language}-${window.screen.width}x${window.screen.height}`;
        let hash = 0;
        for (let i = 0; i < data.length; i++) {
            const char = data.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16);
    }, []);

    // Verificar dispositivo al montar
    useEffect(() => {
        const checkDeviceTrust = async () => {
            try {
                const fingerprint = generateDeviceFingerprint();
                const response = await apiRequest(
                    `${API_ENDPOINTS.chatDeviceVerify}?fingerprint=${fingerprint}`
                );

                if (response.success && response.data.trusted) {
                    // Dispositivo ya confiado - mostrar animacion corta
                    setIsFirstTimeDevice(false);
                    setShowLoadingAnimation(true);
                    setSecurityState('loading');
                } else {
                    // Primera vez o dispositivo no confiado
                    setIsFirstTimeDevice(true);
                    setShowSecurityModal(true);
                    setSecurityState('needsVerification');
                }
            } catch (error) {
                console.error('Error verificando dispositivo:', error);
                // Si hay error (tabla no existe, etc), mostrar modal
                setShowSecurityModal(true);
                setSecurityState('needsVerification');
            }
        };

        checkDeviceTrust();
    }, [generateDeviceFingerprint]);

    // Handler cuando se acepta el modal de seguridad
    const handleSecurityAccept = async (trustData) => {
        setShowSecurityModal(false);

        try {
            // Detectar browser y OS
            const userAgent = navigator.userAgent;
            let browser = 'Desconocido';
            if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) browser = 'Chrome';
            else if (userAgent.includes('Firefox')) browser = 'Firefox';
            else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
            else if (userAgent.includes('Edg')) browser = 'Edge';

            let os = 'Desconocido';
            if (userAgent.includes('Windows')) os = 'Windows';
            else if (userAgent.includes('Mac')) os = 'macOS';
            else if (userAgent.includes('Linux')) os = 'Linux';
            else if (userAgent.includes('Android')) os = 'Android';
            else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';

            // Registrar dispositivo como confiado
            if (trustData.trustLevel === 'personal') {
                await apiRequest(API_ENDPOINTS.chatDeviceTrust, {
                    method: 'POST',
                    body: JSON.stringify({
                        fingerprint: trustData.fingerprint,
                        deviceName: `${browser} en ${os}`,
                        browser,
                        os,
                        ipAddress: trustData.deviceInfo?.ip,
                        location: trustData.deviceInfo?.location,
                        rememberDays: 30,
                        termsAccepted: true
                    })
                });
            } else if (trustData.trustLevel === 'shared') {
                // Log de acceso sin guardar dispositivo
                await apiRequest(API_ENDPOINTS.chatDeviceLogAccess, {
                    method: 'POST',
                    body: JSON.stringify({
                        fingerprint: trustData.fingerprint,
                        browser,
                        os,
                        ipAddress: trustData.deviceInfo?.ip,
                        location: trustData.deviceInfo?.location
                    })
                });
            }

            // Mostrar animacion de carga
            setShowLoadingAnimation(true);
            setSecurityState('loading');
        } catch (error) {
            console.error('Error registrando dispositivo:', error);
            // Continuar de todas formas
            setShowLoadingAnimation(true);
            setSecurityState('loading');
        }
    };

    // Handler cuando se cancela el modal de seguridad
    const handleSecurityCancel = () => {
        setShowSecurityModal(false);
        onClose();
    };

    // Handler cuando termina la animacion de carga
    const handleLoadingComplete = () => {
        setShowLoadingAnimation(false);
        setSecurityState('ready');
    };

    // Cargar conversaciones
    const fetchConversations = useCallback(async () => {
        try {
            const response = await apiRequest(API_ENDPOINTS.chatConversations);
            if (response.success) {
                setConversations(response.data);
                const totalUnread = response.data.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
                onUnreadChange?.(totalUnread);
            }
        } catch (error) {
            console.error('Error al cargar conversaciones:', error);
        } finally {
            setLoading(false);
        }
    }, [onUnreadChange]);

    // Solo cargar conversaciones cuando el estado de seguridad es 'ready'
    useEffect(() => {
        if (securityState !== 'ready') return;

        fetchConversations();
        const interval = setInterval(fetchConversations, 10000);
        return () => clearInterval(interval);
    }, [fetchConversations, securityState]);

    // Cargar mensajes de conversacion activa
    const fetchMessages = useCallback(async (conversationId) => {
        if (!conversationId) return;

        setLoadingMessages(true);
        try {
            const response = await apiRequest(API_ENDPOINTS.chatMessages(conversationId));
            if (response.success) {
                setMessages(response.data);
                await apiRequest(API_ENDPOINTS.chatMarkRead(conversationId), { method: 'POST' });
                fetchConversations();
            }
        } catch (error) {
            console.error('Error al cargar mensajes:', error);
        } finally {
            setLoadingMessages(false);
        }
    }, [fetchConversations]);

    useEffect(() => {
        if (activeConversation) {
            fetchMessages(activeConversation.id);
            const interval = setInterval(() => fetchMessages(activeConversation.id), 5000);
            return () => clearInterval(interval);
        }
    }, [activeConversation, fetchMessages]);

    // Seleccionar conversacion
    const handleSelectConversation = (conversation) => {
        setActiveConversation(conversation);
        setReplyTo(null);
        if (isMobile) {
            setShowSidebar(false);
        }
    };

    // Volver a la lista (movil)
    const handleBack = () => {
        setShowSidebar(true);
        setActiveConversation(null);
        setReplyTo(null);
    };

    // Enviar mensaje
    const handleSendMessage = async (messageData) => {
        if (!activeConversation) return;

        try {
            let response;

            if (messageData.file) {
                const formData = new FormData();
                formData.append('file', messageData.file);
                if (messageData.contentIv) {
                    formData.append('contentIv', messageData.contentIv);
                }
                if (messageData.replyToId) {
                    formData.append('replyToId', messageData.replyToId);
                }

                response = await fetch(API_ENDPOINTS.chatMessagesFile(activeConversation.id), {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: formData
                });
                response = await response.json();
            } else {
                response = await apiRequest(API_ENDPOINTS.chatMessages(activeConversation.id), {
                    method: 'POST',
                    body: JSON.stringify({
                        content: messageData.content,
                        contentIv: messageData.contentIv,
                        replyToId: messageData.replyToId
                    })
                });
            }

            if (response.success) {
                fetchMessages(activeConversation.id);
            }
        } catch (error) {
            console.error('Error al enviar mensaje:', error);
        }
    };

    // Crear nueva conversacion
    const handleNewConversation = async (data) => {
        try {
            let response;

            if (data.type === 'individual') {
                response = await apiRequest(API_ENDPOINTS.chatConversationIndividual, {
                    method: 'POST',
                    body: JSON.stringify({ targetUserId: data.targetUserId })
                });
            } else {
                response = await apiRequest(API_ENDPOINTS.chatConversationGroup, {
                    method: 'POST',
                    body: JSON.stringify({
                        name: data.name,
                        description: data.description,
                        memberIds: data.memberIds
                    })
                });
            }

            if (response.success) {
                setShowNewChat(false);
                fetchConversations();
                const newConvId = response.data.conversationId;
                const newConv = { id: newConvId, ...data };
                setActiveConversation(newConv);
            }
        } catch (error) {
            console.error('Error al crear conversacion:', error);
        }
    };

    // Responder mensaje
    const handleReply = (message) => {
        setReplyTo(message);
    };

    // ========================================================
    // RENDERIZADO
    // ========================================================

    // Mostrar modal de seguridad
    if (showSecurityModal) {
        return (
            <ChatSecurityModal
                isOpen={true}
                onAccept={handleSecurityAccept}
                onCancel={handleSecurityCancel}
                userId={user.id}
            />
        );
    }

    // Mostrar animacion de carga
    if (showLoadingAnimation) {
        return (
            <ChatLoadingAnimation
                isVisible={true}
                onComplete={handleLoadingComplete}
                isFirstTime={isFirstTimeDevice}
            />
        );
    }

    // Toggle fullscreen con animación 3D de salida
    const toggleFullscreen = () => {
        if (isFullscreen) {
            // Iniciar animación de salida
            setIsExitingFullscreen(true);
            setTimeout(() => {
                setIsFullscreen(false);
                setIsExitingFullscreen(false);
            }, 300); // Duración de urbanq-fullscreen-exit-3d
        } else {
            setIsFullscreen(true);
        }
    };

    // Chat window principal (solo cuando securityState === 'ready')
    return (
        <div className={`urbanq-chat-window ${isFullscreen ? 'fullscreen' : ''} ${isExitingFullscreen ? 'fullscreen-exit' : ''}`}>
            {/* Sidebar con lista de conversaciones */}
            <div className={`urbanq-chat-sidebar ${!showSidebar && isMobile ? 'hidden' : ''}`}>
                <div className="urbanq-chat-sidebar-header">
                    <h3>Mensajes</h3>
                    <div className="urbanq-chat-sidebar-actions">
                        <button
                            className="urbanq-chat-btn-icon"
                            onClick={() => setShowNewChat(true)}
                            title="Nueva conversacion"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                            </svg>
                        </button>
                        <button
                            className="urbanq-chat-btn-icon"
                            onClick={toggleFullscreen}
                            title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
                        >
                            {isFullscreen ? (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                </svg>
                            )}
                        </button>
                        <button
                            className="urbanq-chat-btn-icon"
                            onClick={onMinimize}
                            title="Minimizar"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 13H5v-2h14v2z"/>
                            </svg>
                        </button>
                        <button
                            className="urbanq-chat-btn-icon"
                            onClick={onClose}
                            title="Cerrar"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <ConversationList
                    conversations={conversations}
                    activeId={activeConversation?.id}
                    onSelect={handleSelectConversation}
                    loading={loading}
                    currentUserId={user.id}
                />
            </div>

            {/* Area de chat */}
            <div className={`urbanq-chat-main ${showSidebar && isMobile ? 'hidden' : ''}`}>
                {activeConversation ? (
                    <>
                        <ChatHeader
                            conversation={activeConversation}
                            onBack={isMobile ? handleBack : null}
                            onClose={!isMobile ? onClose : null}
                        />
                        <MessageList
                            messages={messages}
                            loading={loadingMessages}
                            currentUserId={user.id}
                            onReply={handleReply}
                        />
                        <MessageInput
                            onSend={handleSendMessage}
                            conversationId={activeConversation.id}
                            replyTo={replyTo}
                            onCancelReply={() => setReplyTo(null)}
                        />
                    </>
                ) : (
                    <div className="urbanq-chat-empty">
                        <div className="urbanq-chat-empty-content">
                            <svg viewBox="0 0 24 24" fill="currentColor" className="urbanq-chat-empty-icon">
                                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                            </svg>
                            <h4>Selecciona una conversacion</h4>
                            <p>O inicia una nueva conversacion con tus vecinos</p>
                            <button
                                className="urbanq-chat-btn-primary"
                                onClick={() => setShowNewChat(true)}
                            >
                                Nueva conversacion
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de nueva conversacion */}
            {showNewChat && (
                <NewChatModal
                    onClose={() => setShowNewChat(false)}
                    onCreate={handleNewConversation}
                    condominiumId={user.condominium_id}
                    currentUserId={user.id}
                />
            )}
        </div>
    );
};

export default ChatWindow;
